import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {SchedulerModel} from '../models/SchedulerModel'
import {
  deleteSchedulerSetting,
  filterDataChange,
  getAll,
  selectSchedulerFilter,
  selectSchedulerList,
  trySchedulerSetting,
  updateSchedulerSettingStatus,
} from '../redux/SchedulerRedux'
import {PaginateModel} from '../../../shared/models/PaginateModel'
import Paginator from '../../../components/Paginator'
import moment from 'moment'
import {SchedulerStatus} from '../constants'
import TrySchedulerModal from './TrySchedulerModal'
import Tooltip from 'react-tooltip-lite'
import ConfirmModal from '../../../components/ConfirmModal'

const ICT_OFFSET = 7 * 60 // ICT is UTC+7 in minutes

const getTimezoneAbbr = (date: Date): string => {
  const str = date.toLocaleTimeString('en-US', {timeZoneName: 'short'})
  const match = str.match(/\s([A-Z]{2,5})$/)
  return match ? match[1] : `UTC${date.getTimezoneOffset() <= 0 ? '+' : '-'}${Math.abs(date.getTimezoneOffset() / 60)}`
}

const formatTimeWithTimezone = (timeStr: string): string => {
  const localOffsetMinutes = -new Date().getTimezoneOffset() // browser offset in minutes from UTC
  if (localOffsetMinutes === ICT_OFFSET) {
    return `${timeStr} ICT`
  }
  const [hours, minutes] = timeStr.split(':').map(Number)
  const totalMinutesICT = hours * 60 + minutes
  // Convert ICT time to local: local = ICT - ICT_OFFSET + localOffset
  let totalMinutesLocal = totalMinutesICT - ICT_OFFSET + localOffsetMinutes
  if (totalMinutesLocal < 0) totalMinutesLocal += 24 * 60
  if (totalMinutesLocal >= 24 * 60) totalMinutesLocal -= 24 * 60
  const localHours = Math.floor(totalMinutesLocal / 60).toString().padStart(2, '0')
  const localMinutes = (totalMinutesLocal % 60).toString().padStart(2, '0')
  const localAbbr = getTimezoneAbbr(new Date())
  return `${timeStr} ICT (${localHours}:${localMinutes} ${localAbbr})`
}

type Props = {
  className?: string
  userId: string
}

const SchedulerTable: FC<Props> = (props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const didRequest = useRef(false)
  const data: SchedulerModel[] = useAppSelector(selectSchedulerList)
  const filter: PaginateModel = useAppSelector(selectSchedulerFilter)
  const [currentScheduler, setCurrentScheduler] = useState<SchedulerModel | null>(null)
  const [schedulerToDelete, setSchedulerToDelete] = useState<SchedulerModel | null>(null)

  useEffect(() => {
    if (!didRequest.current) {
      dispatch(getAll())
      didRequest.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePageChanged = (targetPage: number) => {
    dispatch(filterDataChange({...filter, page: targetPage}))
  }

  const handleTryScheduler = (item: SchedulerModel) => {
    setCurrentScheduler(item)
  }

  const handleTryConfirm = (folderName: string) => {
    if (currentScheduler) {
      // Add your API call here to execute the scheduler
      dispatch(trySchedulerSetting({folderName, schedulerId: currentScheduler.id.toString()}))
    }
  }

  const handleDeleteConfirm = () => {
    if (schedulerToDelete) {
      dispatch(deleteSchedulerSetting(schedulerToDelete.id))
      setSchedulerToDelete(null)
    }
  }

  const renderItemActions = (item: SchedulerModel) => {
    let actions = []

    if (item.status === SchedulerStatus.ACTIVE) {
      actions = [
        <Tooltip
          content={<div>{intl.formatMessage({id: 'Scheduler.ACTION.TRY'})}</div>}
          className='tooltips'
        >
          <button
            key='try'
            className='btn btn-icon btn-light-primary btn-sm me-1'
            onClick={() => handleTryScheduler(item)}
            data-bs-toggle='tooltip'
          >
            <i className='bi bi-play-circle fs-4'></i>
          </button>
        </Tooltip>,
        <Tooltip
          content={<div>{intl.formatMessage({id: 'Scheduler.STATUS.INACTIVE'})}</div>}
          className='tooltips'
        >
          <button
            key='status'
            className='btn btn-icon btn-light-danger btn-sm'
            onClick={() => {
              dispatch(
                updateSchedulerSettingStatus({id: item.id, status: SchedulerStatus.INACTIVE})
              )
            }}
          >
            <i className='bi bi-x-circle fs-4'></i>
          </button>
        </Tooltip>,
      ]
    } else {
      actions = [
        <Tooltip
          content={<div>{intl.formatMessage({id: 'Scheduler.STATUS.ACTIVE'})}</div>}
          className='tooltips'
        >
          <button
            key='status'
            className='btn btn-icon btn-light-success btn-sm'
            onClick={() => {
              dispatch(updateSchedulerSettingStatus({id: item.id, status: SchedulerStatus.ACTIVE}))
            }}
          >
            <i className='bi bi-check-circle fs-4'></i>
          </button>
        </Tooltip>,
      ]
    }
    actions.push(
      <Tooltip
        content={<div>{intl.formatMessage({id: 'GENERAL.DELETE'})}</div>}
        className='tooltips'
      >
        <button
          key='delete'
          className='btn btn-icon btn-light-danger btn-sm ms-1'
          onClick={() => setSchedulerToDelete(item)}
        >
          <i className='bi bi-trash fs-4'></i>
        </button>
      </Tooltip>
    )
    return <div className='d-flex justify-content-end flex-shrink-0'>{actions}</div>
  }

  const renderSchedulerItem = (item: SchedulerModel) => {
    return (
      <tr key={item.id}>
        <td> {item.id.toString()}</td>
        <td> {item.name} </td>
        <td> {item.folderType} </td>
        <td> {item.folderName} </td>
        <td> {formatTimeWithTimezone(item.time)} </td>
        <td> {item.setting} </td>
        <td>{intl.formatMessage({id: 'Scheduler.TYPE.' + item.type})}</td>
        <td>
          <span
            className={`badge badge-light-${
              item.status === SchedulerStatus.ACTIVE ? 'success' : 'danger'
            } d-inline-flex align-items-center`}
          >
            {intl.formatMessage({id: 'Scheduler.STATUS.' + item.status})}
          </span>
        </td>
        <td> {moment(item.createdAt).format(process.env.REACT_APP_DATE_FORMAT)} </td>
        <td>{renderItemActions(item)}</td>
      </tr>
    )
  }
  return (
    <>
      <div className='table-responsive'>
        {/* begin::Table */}
        <table className='table table-hover table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
          {/* begin::Table head */}
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.ID'})}</th>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.DESCRIPTION'})}</th>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.FOLDER'})}</th>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.FOLDERNAME'})}</th>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.TIME'})}</th>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.SETTING'})}</th>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.SCHEDULER_TYPE'})}</th>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.STATUS'})}</th>
              <th>{intl.formatMessage({id: 'Scheduler.TABLE.CREATE_AT'})}</th>
              <th className='text-end'>{intl.formatMessage({id: 'Scheduler.TABLE.ACTION'})}</th>
            </tr>
          </thead>
          {/* end::Table head */}
          {/* begin::Table body */}
          <tbody>{data?.map((item: any) => renderSchedulerItem(item))}</tbody>
          {/* end::Table body */}
        </table>
        {/* end::Table */}
      </div>
      <div className='d-flex justify-content-end pt-10'>
        <Paginator
          currentPage={filter.page}
          totalPages={filter.pageCount || 1}
          onPageChange={handlePageChanged}
        />
      </div>
      <TrySchedulerModal
        show={currentScheduler !== null}
        onHide={() => setCurrentScheduler(null)}
        onConfirm={handleTryConfirm}
        scheduler={currentScheduler}
      />
      <ConfirmModal
        onClose={() => setSchedulerToDelete(null)}
        isShow={schedulerToDelete !== null}
        onConfirm={handleDeleteConfirm}
        message={intl.formatMessage({id: 'GENERAL.DELETE.CONFIRM'})}
      />
    </>
  )
}

export default SchedulerTable
